import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi } from '../hooks/useApi';
import { useTranslation } from '../hooks/useTranslation';
import { useDisplaySettings } from '../hooks/useDisplaySettings';

interface WeatherData {
  current: {
    temp_c: number;
    condition: string;
    icon: string;
    humidity: number;
    wind_kph: number;
    feelslike_c: number;
  };
  forecast: {
    date: string;
    maxtemp_c: number;
    mintemp_c: number;
    condition: string;
    icon: string;
  }[];
}

// Map weather conditions to emoji
function weatherEmoji(condition: string): string {
  const c = condition.toLowerCase();
  if (c.includes('sunny') || c.includes('clear')) return '\u2600\uFE0F';
  if (c.includes('partly cloudy')) return '\u26C5';
  if (c.includes('cloudy') || c.includes('overcast')) return '\u2601\uFE0F';
  if (c.includes('rain') || c.includes('drizzle')) return '\u{1F327}\uFE0F';
  if (c.includes('thunder')) return '\u26C8\uFE0F';
  if (c.includes('snow') || c.includes('sleet') || c.includes('ice')) return '\u{1F328}\uFE0F';
  if (c.includes('fog') || c.includes('mist')) return '\u{1F32B}\uFE0F';
  return '\u{1F324}\uFE0F';
}

export function WeatherWidget() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { settings } = useDisplaySettings();
  const weatherUrl = settings.weatherLocation
    ? `/api/v1/user/weather?q=${encodeURIComponent(settings.weatherLocation)}`
    : '/api/v1/user/weather';
  const { data, isLoading } = useApi<WeatherData>(weatherUrl);

  // T6.7 / D8: `backdropFilter: 'blur(8px)'` removed — `--caro-card-bg`
  // is opaque in every theme so the blur had no visible effect.
  const cardStyle: React.CSSProperties = {
    backgroundColor: 'var(--caro-card-bg)',
    borderRadius: '16px',
    padding: '14px 20px',
    cursor: 'pointer',
    boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
    border: '1px solid var(--caro-border)',
    minWidth: '160px',
  };

  // Loading or unavailable — show placeholder card
  if (isLoading || !data?.current) {
    return (
      <div onClick={() => navigate('/weather')} style={cardStyle}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <span style={{ fontSize: 'var(--font-heading)' }}>{'\u{1F324}\uFE0F'}</span>
          <div>
            <div style={{ fontSize: 'var(--font-body)', fontWeight: 600, color: 'var(--caro-text)' }}>
              {t.weather.title}
            </div>
            <div style={{ fontSize: 'var(--font-body)', color: 'var(--caro-text-light)' }}>
              {isLoading ? t.common.loading : t.weather.unavailable}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const { current, forecast } = data;
  const todayForecast = forecast?.[0];

  return (
    <div onClick={() => navigate('/weather')} style={cardStyle}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
        <span style={{ fontSize: 'var(--font-heading)' }}>{weatherEmoji(current.condition)}</span>
        <div>
          <div style={{
            fontSize: 'var(--font-heading)',
            fontWeight: 700,
            color: 'var(--caro-text)',
            lineHeight: 1,
          }}>
            {Math.round(current.temp_c)}&deg;
          </div>
          <div style={{
            fontSize: 'var(--font-body)',
            color: 'var(--caro-text-light)',
            marginTop: '2px',
          }}>
            {current.condition}
          </div>
        </div>
      </div>
      {todayForecast && (
        <div style={{
          display: 'flex',
          gap: '8px',
          marginTop: '8px',
          fontSize: 'var(--font-body)',
          color: 'var(--caro-text-light)',
        }}>
          <span>{t.weather.high} {Math.round(todayForecast.maxtemp_c)}&deg;</span>
          <span>{t.weather.low} {Math.round(todayForecast.mintemp_c)}&deg;</span>
        </div>
      )}
    </div>
  );
}
